import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../shared/services/user.service';
import { NgxDeeplinkerService } from 'ngx-deeplinker';
import { GlobalAlertService } from '../shared/services/global-alert.service';
import { UserDefaults, UserStatus, User } from '../shared/model/user.model';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PermissionService } from '../shared/services/permissions.service';
import { BusinessRolesService } from '../shared/services/business-roles.service';
import { UserFormService } from '../shared/services/user-form.service';
import { UserSubmitService } from '../shared/services/user-submit.service';

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss', '../shared/shared.styles.scss'],
})
export class EditComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  appPrefix = 'usr';
  selectedId = '';
  isSubmitting = false;
  userStatus$: Observable<UserStatus>;
  subscriptions: Subscription[] = [];
  canSubmitRoles = false;
  isOnboarding = false;

  readonly mode = 'edit';

  user: User;
  loadingUserDetails = true;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: GlobalAlertService,
    private userService: UserService,
    private ngxDeeplinkerService: NgxDeeplinkerService,
    private permissionService: PermissionService,
    private businessRolesService: BusinessRolesService,
    private userFormService: UserFormService,
    private userSubmitService: UserSubmitService,
    private cdr: ChangeDetectorRef,
  ) {
    this.userStatus$ = this.userService.user$.pipe(map((user) => user.status));
    this.userForm = this.userFormService.createUserForm(UserDefaults, true);
    this.userFormService.disableFields(this.userForm, true);
    this.userFormService.disableSyncToEcrm(this.userForm, { ...UserDefaults });
  }

  ngOnInit() {
    const routeSub = this.route.params.subscribe(async (params) => {
      if (params.id) {
        this.selectedId = params.id;
        this.userService.selectedUserId$.next(this.selectedId);
        this.loadUser(this.selectedId);
      }
    });

    const permissionSub = this.permissionService.permissions$.subscribe((permissions) => {
      if (permissions) {
        if (this.permissionService.canSubmitRoles()) {
          this.canSubmitRoles = true;
        }
        if (this.permissionService.isOnboarding()) {
          this.isOnboarding = true;
        }
      }
    });

    this.subscriptions.push(...[routeSub, permissionSub]);
  }

  async loadUser(userId: string) {
    try {
      this.loadingUserDetails = true;
      const userData = await this.userService.getUser(userId);
      this.user = userData;
      this.userForm.patchValue(this.user);
      this.userForm.setControl('phones', this.userFormService.createPhonesFormArray(this.user.phones));
      this.userFormService.initialValidation(this.userForm, this.user);
      this.loadingUserDetails = false;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading user:', error);
      this.messageService.setError('Failed to load user data');
    }
  }

  async handleSubmit() {
    this.isSubmitting = true;
    try {
      await this.userSubmitService.handleEditSubmit(
        this.userForm,
        this.selectedId,
        this.canSubmitRoles,
        this.isOnboarding,
        true,
      );
    } finally {
      this.isSubmitting = false;
    }
  }

  handleCancel() {
    this.ngxDeeplinkerService.returnHandler({ appPrefix: this.appPrefix });
    setTimeout(() => {
      this.businessRolesService.clearNewSpaces$.next(true);
      this.businessRolesService.newSpacesWithRoles$.next([]);
    }, 10);
    if (this.selectedId) {
      this.router.navigate([`details/${this.selectedId}/view`], {});
    } else {
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
